import React from "react";
import moment from "moment-timezone";
import { v1 as uuidv1 } from "uuid";

import { fetchApi, withAccessToken } from "../../Network/NetworkService";
import { endpoint } from "../../Constants";
import { notif } from "../Ui/Notification";

import "../../Assets/Pages/dermatoSdr.css";
import IconTooltip from "../Ui/IconTooltip";

// Keep in sync with MAX_RANGE_DAYS in GdprWarningController.js.
const MAX_RANGE_DAYS = 31;

// Same rule as isValidTestEmail in GdprWarningController.js — basic shape +
// printable ASCII — so the client and server agree on what a valid test
// address is (the server re-checks and aborts on mismatch regardless).
const isValidTestEmail = (s) => {
  const v = (s || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !/[^\x20-\x7E]/.test(v);
};

// GDPR inactive-account warning campaign: targets users whose LAST activity is
// an incomplete request inside the selected window — they never completed any
// consultation and never came back since. The email warns that their account
// will be deleted per the data-retention policy unless they log back in.
class GdprWarning extends React.Component {
  constructor() {
    super();
    const today = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    this.state = {
      // "incomplete": last activity is an incomplete request in the window.
      // "noRequest": zero requests ever, last login in the window.
      population: "incomplete",
      startDate: today,
      endDate: today,
      testMode: false,
      testEmail: "",
      users: [],
      loading: false,
      sending: false,
      sendProgress: "0",
      usersLoaded: false,
      sendEvent: null,
      page: 1,
      totalPage: 0,
      countUsers: 0,
    };
  }

  componentDidMount() {
    this.fetchUsers();
  }

  componentWillUnmount() {
    if (this.state.sendEvent) {
      this.state.sendEvent.close();
    }
  }

  rangeDays = () =>
    moment(this.state.endDate).diff(moment(this.state.startDate), "days") + 1;

  rangeTooWide = () => this.rangeDays() > MAX_RANGE_DAYS;

  fetchUsers = () => {
    if (this.rangeTooWide()) {
      this.setState({ users: [], usersLoaded: true, loading: false });
      return;
    }
    this.setState({ loading: true });
    const url =
      "/gdpr-warning/list?start=" +
      this.state.startDate +
      "&end=" +
      this.state.endDate +
      "&population=" +
      this.state.population +
      "&page=" +
      this.state.page;
    fetchApi(url)
      .then((res) => {
        // Drop stale responses from the other tab: the server echoes the
        // population it answered for. Without this, a slow in-flight fetch
        // resolving after a tab switch overwrites the table AND countUsers —
        // which feeds the send-confirmation dialog.
        if (res.population && res.population !== this.state.population) {
          return;
        }
        this.setState({
          users: res.users || [],
          totalPage: res.countPages || 0,
          countUsers: res.countUsers || 0,
          usersLoaded: true,
          loading: false,
        });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération des utilisateurs",
          "warning",
        );
        this.setState({ loading: false });
      });
  };

  switchPopulation = (population) => {
    if (this.state.population === population || this.state.sending) return;
    this.setState(
      { population, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleStartChange = (e) => {
    const startDate = e.target.value;
    // Keep end >= start so the range is always valid.
    const endDate =
      moment(this.state.endDate).isBefore(moment(startDate)) ||
      !this.state.endDate
        ? startDate
        : this.state.endDate;
    this.setState(
      { startDate, endDate, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleEndChange = (e) => {
    this.setState(
      { endDate: e.target.value, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  updatePage(page) {
    this.setState({ users: [], page, loading: true }, () => {
      this.fetchUsers();
    });
  }

  sendToAll = () => {
    // A real campaign needs recipients in the list; test mode sends to a single
    // test address and doesn't depend on the cohort, so don't block it on it.
    if (
      this.state.sending ||
      this.rangeTooWide() ||
      (!this.state.testMode && !this.state.users.length)
    )
      return;

    const { testMode, testEmail, countUsers } = this.state;
    const trimmedTest = (testEmail || "").trim();

    if (testMode && !isValidTestEmail(trimmedTest)) {
      notif.addNotification(
        "Erreur",
        "Renseignez une adresse email de test valide (caractères ASCII).",
        "warning",
      );
      return;
    }

    // Confirmation before any real send — guards against an accidental click on
    // a large recipient set.
    const confirmMsg = testMode
      ? "Mode TEST : envoyer 1 email d'exemple à " +
        trimmedTest +
        " ? Aucun utilisateur réel contacté."
      : "Envoyer l'avertissement RGPD (suppression de compte inactif) à " +
        countUsers +
        " utilisateur(s) ? De vrais emails seront envoyés.";
    if (!window.confirm(confirmMsg)) return;

    const tokenId = uuidv1();
    const sendEvent = new EventSource(
      withAccessToken(endpoint + "/gdpr-warning/sendProgress?token=" + tokenId),
    );
    this.setState({ sending: true, sendProgress: "0", sendEvent });

    let posted = false;
    let settled = false;
    let fallbackTimer = null;
    let watchdog = null;

    const reset = () => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      clearTimeout(watchdog);
      this.setState({ sending: false, sendProgress: "0", sendEvent: null });
      sendEvent.close();
    };

    const refreshAfterSend = () => {
      this.setState({ page: 1 }, this.fetchUsers);
    };

    // If the progress stream goes silent (frames buffered by a mobile/cellular
    // proxy), the send still ran server-side and is recorded in the history —
    // don't let the spinner hang. Restart on each frame; fire if idle too long.
    const bumpWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        if (settled) return;
        notif.addNotification(
          "Info",
          "Envoi lancé. Consultez l'historique pour le résultat.",
          "info",
        );
        reset();
        refreshAfterSend();
      }, 30000);
    };

    // The POST triggers the send; the SSE stream is only live progress. Fire on
    // `onopen` with a timeout fallback so the send can't be silently blocked by
    // proxies that buffer text/event-stream (see DermatoSdr.jsx).
    const triggerSend = () => {
      if (posted) return;
      posted = true;
      clearTimeout(fallbackTimer);
      bumpWatchdog();
      fetchApi("/gdpr-warning/send", {
        method: "POST",
        body: JSON.stringify({
          start: this.state.startDate,
          end: this.state.endDate,
          id: tokenId,
          population: this.state.population,
          testMode: testMode,
          testEmail: testMode ? trimmedTest : null,
        }),
      }).catch(() => {
        notif.addNotification("Erreur", "Erreur d'envoi", "warning");
        reset();
      });
    };

    fallbackTimer = setTimeout(triggerSend, 5000);
    sendEvent.onopen = triggerSend;

    sendEvent.onmessage = (ev) => {
      if (posted) bumpWatchdog();
      // Some setups deliver the data frame before onopen; treat it identically.
      if (ev.data === "start") {
        triggerSend();
        return;
      }
      if (ev.data.startsWith("fail&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        const failCount = parseInt(parts[2]) || 0;
        notif.addNotification(
          "Error",
          total + " envoyés, " + failCount + " échecs",
          "warning",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data.includes("error")) {
        notif.addNotification(
          "Erreur",
          ev.data.split("&")[1] || "Erreur d'envoi",
          "warning",
        );
        reset();
      } else if (ev.data.startsWith("done&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        notif.addNotification(
          "Succès",
          testMode
            ? "Email de test envoyé à " + trimmedTest
            : total + " avertissements RGPD envoyés",
          "success",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data === "empty") {
        notif.addNotification("Info", "Aucun utilisateur à traiter", "info");
        reset();
      } else {
        this.setState({ sendProgress: ev.data });
      }
    };

    sendEvent.onerror = () => {
      // Pre-POST: the timeout fallback still fires the send. Post-POST: the
      // send continues server-side and the watchdog clears the spinner.
    };
  };

  render() {
    const {
      startDate,
      endDate,
      users,
      loading,
      sending,
      sendProgress,
      usersLoaded,
      page,
      totalPage,
      countUsers,
      testMode,
      testEmail,
    } = this.state;
    const maxDate = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    const tooWide = this.rangeTooWide();
    const population = this.state.population;
    const isNoRequest = population === "noRequest";

    return (
      <div className="dermato-sdr-container">
        <div className="dermato-sdr-header">
          <h2 className="dermato-sdr-title">{"RGPD — Comptes inactifs"}</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() =>
                this.props.history.push("/gdpr-warning/reactivations")
              }
            >
              {"Réactivations"}
            </button>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() => this.props.history.push("/gdpr-warning/historique")}
            >
              {"Historique des envois"}
            </button>
          </div>
        </div>

        <div className="dermato-sdr-tabs">
          <button
            className={
              "dermato-sdr-tab" +
              (!isNoRequest ? " dermato-sdr-tab-active" : "")
            }
            onClick={() => this.switchPopulation("incomplete")}
            disabled={sending}
          >
            {"Requêtes incomplètes"}
          </button>
          <button
            className={
              "dermato-sdr-tab" + (isNoRequest ? " dermato-sdr-tab-active" : "")
            }
            onClick={() => this.switchPopulation("noRequest")}
            disabled={sending}
          >
            {"Sans aucune requête"}
          </button>
        </div>

        <div className="dermato-sdr-card">
          <h3 className="dermato-sdr-card-title">Filtres</h3>
          <p style={{ color: "#888", fontSize: 14, marginTop: -8 }}>
            {isNoRequest
              ? "Utilisateurs inscrits mais n'ayant JAMAIS créé de requête, dont la dernière connexion est dans la période choisie. L'email les avertit que leur compte sera supprimé (politique de conservation des données) sauf reconnexion."
              : "Utilisateurs dont la DERNIÈRE activité est une requête incomplète dans la période choisie : jamais de consultation aboutie, aucune activité depuis. L'email les avertit que leur compte sera supprimé (politique de conservation des données) sauf reconnexion."}
          </p>
          <div className="dermato-sdr-inline">
            <div className="dermato-sdr-form-group">
              <label>Du</label>
              <input
                type="date"
                className="dermato-sdr-input"
                value={startDate}
                max={maxDate}
                onChange={this.handleStartChange}
              />
            </div>
            <div className="dermato-sdr-form-group">
              <label>Au</label>
              <input
                type="date"
                className="dermato-sdr-input"
                value={endDate}
                min={startDate}
                max={maxDate}
                onChange={this.handleEndChange}
              />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <button
                className="dermato-sdr-btn dermatosdr-btn-primary"
                onClick={this.sendToAll}
                disabled={sending || tooWide || (!testMode && !users.length)}
              >
                {sending
                  ? "Envoi... " + sendProgress + "%"
                  : testMode
                    ? "Envoyer un test"
                    : "Envoyer l'avertissement RGPD"}
              </button>
            </div>
          </div>

          <div className="dermato-sdr-testmode">
            <label className="dermato-sdr-checkbox">
              <input
                type="checkbox"
                checked={testMode}
                disabled={sending}
                onChange={(e) => this.setState({ testMode: e.target.checked })}
              />
              <span>{"Mode test"}</span>
            </label>
            {testMode && (
              <React.Fragment>
                <input
                  type="email"
                  className="dermato-sdr-input"
                  style={{ maxWidth: 260 }}
                  placeholder="email de test"
                  value={testEmail}
                  disabled={sending}
                  onChange={(e) => this.setState({ testEmail: e.target.value })}
                />
                <span style={{ color: "#888", fontSize: 13 }}>
                  {
                    "1 email d'exemple envoyé à cette adresse — aucun utilisateur réel contacté. Le lien du bouton est le même que l'envoi réel (page de connexion)."
                  }
                </span>
              </React.Fragment>
            )}
          </div>
          {tooWide && (
            <p style={{ color: "#c62828", fontSize: 14, marginTop: 8 }}>
              {"Période trop large : maximum " + MAX_RANGE_DAYS + " jours."}
            </p>
          )}
        </div>

        <div className="dermato-sdr-card" style={{ marginTop: 24 }}>
          <h3 className="dermato-sdr-card-title">
            Utilisateurs ({countUsers})
          </h3>

          {loading ? (
            <div className="dermato-sdr-empty">
              <span className="loader" />
            </div>
          ) : usersLoaded && users.length === 0 ? (
            <div className="dermato-sdr-empty">
              {"Aucun utilisateur inactif trouvé pour cette période."}
            </div>
          ) : users.length > 0 ? (
            <div>
              <table className="dermato-sdr-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>
                      {isNoRequest
                        ? "Dernière connexion"
                        : "Dernière activité"}
                    </th>
                    {!isNoRequest && <th>{"Requêtes incomplètes"}</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td>
                        {u.FName} {u.LName}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {moment(u.latestRequestDate).format("DD/MM/YYYY HH:mm")}
                      </td>
                      {!isNoRequest && <td>{u.requestCount}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="dermatosdr-pagination">
                <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
                  Page <strong>{page}</strong> / {totalPage}
                </p>
                <div>
                  {page > 1 && (
                    <IconTooltip
                      icon="fa fa-chevron-left"
                      text="Page précédente"
                      onClick={() => this.updatePage(page - 1)}
                    />
                  )}
                  {page < totalPage && (
                    <IconTooltip
                      icon="fa fa-chevron-right"
                      text="Page suivante"
                      onClick={() => this.updatePage(page + 1)}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default GdprWarning;
